import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "../../../../ui/components/Table";
import { Loader } from "../../../../ui/components/Loader";
import { Badge } from "../../../../ui/components/Badge";
import { useToast } from "../../../../shared/presentation/components";
import {
  getItcReport,
  type ItcReport,
  type ItcReportLine,
} from "../../data/api/itcReportApi";

const money = (v: string | number | null | undefined): string => {
  const n = typeof v === "string" ? parseFloat(v) : v ?? 0;
  return `₹${(Number.isFinite(n as number) ? (n as number) : 0).toLocaleString(
    "en-IN",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  )}`;
};

// Indian financial year (Apr 1 – Mar 31). Returns e.g. { label:"2026-27",
// from:"2026-04-01", to:"2027-03-31" } for a given start year.
const fyFor = (startYear: number) => ({
  label: `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`,
  from: `${startYear}-04-01`,
  to: `${startYear + 1}-03-31`,
});

const currentFyStartYear = (): number => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};

const STATUS_BADGE: Record<
  string,
  { label: string; variant: "success" | "error" | "neutral" }
> = {
  eligible: { label: "Eligible", variant: "success" },
  ineligible_section17: { label: "Blocked · 17(5)", variant: "error" },
  ineligible_others: { label: "Ineligible · Other", variant: "neutral" },
};

const Tile: React.FC<{ label: string; value: string; sub?: string }> = ({
  label,
  value,
  sub,
}) => (
  <div className="flex min-w-[140px] flex-1 flex-col gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-3">
    <span className="text-caption font-caption text-subtext-color">
      {label}
    </span>
    <span className="text-heading-3 font-heading-3 text-default-font">
      {value}
    </span>
    {sub ? (
      <span className="text-caption font-caption text-subtext-color">{sub}</span>
    ) : null}
  </div>
);

export const ItcRegisterSection: React.FC = () => {
  const { showError } = useToast();

  const fyOptions = useMemo(() => {
    const start = currentFyStartYear();
    return [start, start - 1, start - 2].map(fyFor);
  }, []);

  const [fyLabel, setFyLabel] = useState(fyOptions[0].label);
  const [report, setReport] = useState<ItcReport | null>(null);
  const [loading, setLoading] = useState(false);

  /**
   * ZOUR-93 — which GST registration's input credit this is.
   *
   * ITC is claimed per GSTIN, in that registration's own GSTR-3B, so merged
   * totals across a client's registrations are not a figure anybody can file.
   * '' = all of them, which is the default and the only thing a
   * single-registration client ever sees (the filter and the column are hidden
   * for them entirely).
   */
  const [locationFilter, setLocationFilter] = useState("");

  const fetchReport = useCallback(async () => {
    const fy = fyOptions.find((f) => f.label === fyLabel) ?? fyOptions[0];
    setLoading(true);
    try {
      setReport(await getItcReport(fy.from, fy.to, locationFilter || undefined));
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to load ITC register",
      );
    } finally {
      setLoading(false);
    }
  }, [fyLabel, fyOptions, locationFilter, showError]);

  useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  const c = report?.counts ?? {};
  const blocked = report?.blocked;

  // ZOUR-93: the server decides this, so the filter, the column and the totals
  // can never disagree about whether this client has several registrations.
  const hasMultipleRegistrations = report?.hasMultiple === true;
  const registrations = report?.registrations ?? [];
  const columnCount = hasMultipleRegistrations ? 7 : 6;

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Input Tax Credit register
          </span>
          <span className="text-body font-body text-subtext-color">
            Per-line ITC eligibility recorded on posted bills. Blocked tax is not
            claimable and feeds your GSTR-2B reversal.
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
        {/* ZOUR-93 — the registration whose credit is being shown. Hidden
            entirely for a client with one registration. */}
        {hasMultipleRegistrations && (
          <select
            className="rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            aria-label="GST registration"
          >
            <option value="">All registrations</option>
            {registrations.map((registration) => (
              <option
                key={registration.zoho_location_id ?? registration.name}
                value={registration.zoho_location_id ?? ""}
              >
                {registration.name}
                {registration.gstin ? ` · ${registration.gstin}` : ""}
              </option>
            ))}
          </select>
        )}
        <select
          className="rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
          value={fyLabel}
          onChange={(e) => setFyLabel(e.target.value)}
        >
          {fyOptions.map((f) => (
            <option key={f.label} value={f.label}>
              FY {f.label}
            </option>
          ))}
        </select>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="flex w-full flex-wrap gap-3">
        <Tile label="Eligible lines" value={String(c.eligible ?? 0)} />
        <Tile
          label="Blocked · 17(5)"
          value={String(c.ineligible_section17 ?? 0)}
        />
        <Tile
          label="Ineligible · Other"
          value={String(c.ineligible_others ?? 0)}
        />
        <Tile
          label="Blocked ITC (not claimable)"
          value={money(blocked?.total)}
          sub={`CGST ${money(blocked?.cgst)} · SGST ${money(
            blocked?.sgst,
          )} · IGST ${money(blocked?.igst)}${
            blocked && blocked.cess > 0 ? ` · Cess ${money(blocked.cess)}` : ""
          }`}
        />
      </div>

      {/* Detail table */}
      <div className="w-full overflow-x-auto">
        <Table
          header={
            <Table.HeaderRow>
              <Table.HeaderCell>Bill date</Table.HeaderCell>
              {hasMultipleRegistrations && (
                <Table.HeaderCell>Registration</Table.HeaderCell>
              )}
              <Table.HeaderCell>Vendor</Table.HeaderCell>
              <Table.HeaderCell>Line</Table.HeaderCell>
              <Table.HeaderCell>HSN/SAC</Table.HeaderCell>
              <Table.HeaderCell>Tax</Table.HeaderCell>
              <Table.HeaderCell>ITC status</Table.HeaderCell>
            </Table.HeaderRow>
          }
        >
          {loading ? (
            <Table.Row>
              <Table.Cell colSpan={columnCount}>
                <div className="flex w-full items-center justify-center py-10">
                  <Loader />
                </div>
              </Table.Cell>
            </Table.Row>
          ) : !report || report.lines.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={columnCount}>
                <div className="flex w-full items-center justify-center py-10">
                  <span className="text-body font-body text-subtext-color">
                    No ITC classifications recorded for FY {fyLabel}
                  </span>
                </div>
              </Table.Cell>
            </Table.Row>
          ) : (
            report.lines.map((l: ItcReportLine, i: number) => {
              const badge = STATUS_BADGE[l.itc_status] ?? STATUS_BADGE.eligible;
              return (
                <Table.Row key={`${l.jobid}-${l.line_index}-${i}`}>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {l.bill_date || "—"}
                    </span>
                  </Table.Cell>
                  {hasMultipleRegistrations && (
                    <Table.Cell>
                      <div className="flex flex-col">
                        <span className="text-body font-body text-default-font">
                          {l.location_name || "—"}
                        </span>
                        {l.location_gstin && (
                          <span className="text-caption font-caption text-subtext-color">
                            {l.location_gstin}
                          </span>
                        )}
                      </div>
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    <span className="text-body font-body text-default-font">
                      {l.party_name || l.zoho_partyid || "—"}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span
                      className="text-body font-body text-default-font"
                      title={l.line_description || ""}
                    >
                      {l.line_description || "—"}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {l.hsn_or_sac || "—"}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-default-font">
                      {money(l.tax_amount)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col gap-0.5">
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                      {l.section17_category ? (
                        <span className="text-caption font-caption text-subtext-color">
                          {l.section17_category}
                        </span>
                      ) : null}
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })
          )}
        </Table>
      </div>
    </div>
  );
};
